import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export type Organization = {
  id: string;
  name: string;
  plan: string;
  credits: number;
  monthly_quota: number;
  quota_reset_at: string | null;
  plan_expires_at: string | null;
  max_analyses: number | null;
  settings: Record<string, unknown>;
};

// Mismo escalafón que `_shared/plan-guard.ts` y que la función SQL `org_del_proyecto`.
// Si cambia en uno, cambia en los tres: criterios distintos = la interfaz enseña una cosa
// y el backend responde otra. Aquí solo se usa ya para las organizaciones PROPIAS; con
// proyecto abierto ordena la función SQL.
const RANGO: Record<string, number> = {
  free: 0, guionista: 1, indie: 2, 'line-producer': 3, pase: 4, productora: 5, estudio: 6,
};

type OrgRow = Organization & { caducada?: boolean };

// Elige entre varias organizaciones con el criterio del backend: primero por plan
// efectivo (una caducada vale como free) y, a igualdad, antes la no caducada.
function elegirOrg(orgs: Organization[]): Organization | null {
  const ahora = Date.now();
  const vivas: OrgRow[] = orgs.map((o) => ({
    ...o,
    caducada: !!o.plan_expires_at && Date.parse(String(o.plan_expires_at)) < ahora,
  }));
  vivas.sort((a, b) => {
    const ra = RANGO[(a.caducada ? 'free' : (a.plan ?? '')) as string] ?? 0;
    const rb = RANGO[(b.caducada ? 'free' : (b.plan ?? '')) as string] ?? 0;
    return rb - ra || Number(a.caducada) - Number(b.caducada);
  });
  return vivas[0] ?? null;
}

/**
 * Organización cuyo plan manda para el usuario actual.
 *
 * QUÉ PLAN MANDA (mismo criterio que `_shared/plan-guard.ts` y que el runtime Node):
 * la organización DUEÑA DEL PROYECTO sobre el que se opera, que es la que paga. Un
 * colaborador invitado usa las funciones de la productora que le invitó EN los
 * proyectos de esa productora, y no en los suyos. Sin proyecto (funciones de cuenta)
 * manda su organización propia.
 *
 * Antes esto hacía `.limit(1).maybeSingle()` sobre organizations, es decir, cogía una
 * fila AL AZAR. Con un usuario en dos organizaciones (caso real: colaborador externo
 * que además tiene la suya) la interfaz pintaba candados incoherentes con lo que luego
 * respondía el backend, y cambiaba entre recargas.
 *
 * @param jobId proyecto sobre el que se está operando. Omitirlo = funciones de cuenta.
 */
export function useOrganization(jobId?: string | null) {
  return useQuery<Organization | null>({
    queryKey: ['organization', jobId ?? null],
    queryFn: async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user?.id;
      if (!userId) throw new Error('Sesión todavía no disponible');

      // Con proyecto abierto, la organización que manda la resuelve el SERVIDOR con
      // `org_del_proyecto` (SECURITY DEFINER, protegida por `job_visible`).
      //
      // Antes esto se resolvía aquí leyendo `organization_members` del dueño del proyecto,
      // y eso no funciona para un MIEMBRO: la RLS de esa tabla solo deja leer tus propias
      // filas o, si eres propietario, las de tu organización. Al abrir el proyecto de un
      // compañero la consulta volvía vacía, el hook devolvía null y la interfaz echaba el
      // candado a todos los módulos, mientras el backend (fail-open, service_role) sí le
      // habría dejado pasar. Rompía justo el multi-seat que se vende en Productora y
      // Estudio. El escalafón de planes vive ahora también dentro de la función SQL.
      if (jobId) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data: delProyecto, error: rpcErr } = await (supabase as any)
          .rpc('org_del_proyecto', { p_job: jobId });
        if (rpcErr) throw rpcErr;
        const fila = ((delProyecto as Organization[] | null) || [])[0];
        // Sin fila = el proyecto no existe o no es visible para quien pregunta. Se cae a
        // las organizaciones propias, que es lo que hacía antes en ese mismo caso: dejar
        // aquí un null pondría candados donde el backend no bloquea.
        if (fila) return fila;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: mems, error: memErr } = await (supabase as any)
        .from('organization_members')
        .select('org_id')
        .eq('user_id', userId);
      if (memErr) throw memErr;

      const orgIds = ((mems as { org_id: string }[] | null) || []).map((m) => m.org_id);
      if (!orgIds.length) return null;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: orgs, error } = await (supabase as any)
        .from('organizations')
        // Columnas EXPLÍCITAS, nunca `*`: los datos de facturación de la organización
        // (`stripe_customer_id`) no los necesita ninguna pantalla y no tienen por qué
        // llegar al navegador de un colaborador invitado. Con `*` viajaban en cada carga.
        .select('id, name, plan, credits, monthly_quota, quota_reset_at, plan_expires_at, max_analyses, settings, extra_projects')
        .in('id', orgIds);
      if (error) throw error;

      return elegirOrg((orgs as Organization[] | null) || []);
    },
  });
}

/**
 * Returns true if the org has an active plan that is in the `allowed` list
 * and the plan has not expired (plan_expires_at is either absent or in the future).
 */
export function isPlanActive(
  org: Organization | null | undefined,
  allowed: string[],
): boolean {
  return (
    !!org &&
    allowed.includes(org.plan) &&
    (!org.plan_expires_at || new Date(org.plan_expires_at) > new Date())
  );
}
